import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentRedirect() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (processed.current) return;
    processed.current = true;

    const orderId = params.get('order');
    if (!orderId) {
      navigate('/', { replace: true });
      return;
    }

    // 1. Auth check
    if (!isAuthenticated) {
      navigate(`/session-expired?reason=auth&order=${orderId}`, { replace: true });
      return;
    }

    // 2. Fetch order + verify payment session is still valid
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/check-status/${orderId}`);
        const order = res.data;
        const status = order.payment_status;
        const expiresAt = order.expires_at ? new Date(order.expires_at) : null;
        const expired = expiresAt && expiresAt < new Date();

        // Already-resolved orders: bounce to friendly page
        if (status !== 'pending') {
          const reason = status === 'paid' || status === 'completed' ? 'finished'
            : status === 'cancelled' || status === 'failed' ? 'cancelled'
            : 'finished';
          navigate(`/session-expired?reason=${reason}&order=${orderId}`, { replace: true });
          return;
        }

        // Soft-switch: QRISly orders render inline on the QR page, which handles expiry/regen itself.
        if (order.payment_provider === 'qrisly') {
          navigate(`/payment/qris?order=${orderId}`, { replace: true });
          return;
        }

        // Duitku path: only a fresh, still-pending session with a payment_url may proceed.
        if (expired || !order.payment_url) {
          const reason = expired ? 'expired' : 'finished';
          navigate(`/session-expired?reason=${reason}&order=${orderId}`, { replace: true });
          return;
        }

        // Valid → hand off to Duitku
        sessionStorage.setItem('payment_redirect', orderId);
        window.location.replace(order.payment_url);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate(`/session-expired?reason=auth&order=${orderId}`, { replace: true });
        } else {
          navigate(`/session-expired?reason=unavailable&order=${orderId}`, { replace: true });
        }
      }
    })();
  }, [authLoading, isAuthenticated, navigate, params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FCFAF8]" data-testid="payment-redirect-page">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#F4C2C2] border-t-[#6B1F2B] rounded-full animate-spin mx-auto mb-4" />
        <p className="font-body text-[#78716C]">Taking you to the payment page…</p>
      </div>
    </div>
  );
}
